import api from 'src/shared/api/axios';
import type { Contract, PaymentDirection } from 'src/types/contract.type';
import type { ContractMilestone, PaymentStatus } from 'src/types/contractMilestone.type';
import type { ActualPayment } from 'src/types/actualPayment.type';
import { toNumberOrNull } from 'src/utils/formatter';

export type { PaymentStatus };

// 서버 Decimal은 string으로 올 수 있어 Number()로 정규화
function normalizeContract(c: any): Contract {
    return {
        ...c,
        contractAmount: toNumberOrNull(c.contractAmount) ?? 0,
        milestones: Array.isArray(c.milestones) ? c.milestones.map(normalizeMilestone) : undefined,
    };
}

function normalizeMilestone(m: any): ContractMilestone {
    return {
        ...m,
        percent: toNumberOrNull(m.percent),
        amountPlan: toNumberOrNull(m.amountPlan),
        dueDate: m.dueDate ?? null,
        note: m.note ?? null,
        payments: Array.isArray(m.payments)
            ? m.payments
                  .map(normalizePayment)
                  .sort((a: ActualPayment, b: ActualPayment) =>
                      String(a.paidAt ?? '').localeCompare(String(b.paidAt ?? '')),
                  )
            : [],
    };
}

// contracts
export async function apiGetContract(projectId: string, contractId: string) {
    const { data } = await api.get(`/projects/${projectId}/contracts/${contractId}`);
    return normalizeContract(data?.data ?? data);
}

export async function apiListContracts(projectId: string, direction?: PaymentDirection) {
    try {
        const { data } = await api.get(`/projects/${projectId}/contracts`, { params: { direction } });
        const rows = (data?.data ?? data) as any[];
        return rows.map(normalizeContract);
    } catch (err: any) {
        // ✅ 404는 "계약이 없음/라우트 없음"을 UX상 빈 목록으로 취급
        if (err && err.response?.status === 404) {
            return [];
        }
        throw err;
    }
}

export type CreateContractInput = {
    direction: PaymentDirection;
    projectCompanyId: string;
    contractName: string;
    contractAmount: number;
    currency?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    contractDate?: string | null;
};

export async function apiCreateContract(projectId: string, input: CreateContractInput) {
    const { data } = await api.post(`/projects/${projectId}/contracts`, input);
    return normalizeContract(data?.data ?? data);
}

export type UpdateContractPatch = Partial<CreateContractInput>;

export async function apiUpdateContract(projectId: string, contractId: string, patch: UpdateContractPatch) {
    const { data } = await api.patch(`/projects/${projectId}/contracts/${contractId}`, patch);
    return normalizeContract(data?.data ?? data);
}

export async function apiDeleteContract(projectId: string, contractId: string) {
    const { data } = await api.delete(`/projects/${projectId}/contracts/${contractId}`);
    return data?.data ?? data;
}

// milestones
export async function apiListContractMilestones(projectId: string, contractId: string) {
    const { data } = await api.get(`/projects/${projectId}/contracts/${contractId}/milestones`);
    const rows = (data?.data ?? data) as any[];
    return rows.map(normalizeMilestone);
}

export type CreateMilestoneInput = {
    seq: number;
    label: string;
    percent?: number | null;
    amountPlan?: number | null;
    dueDate?: string | null;
    status?: PaymentStatus;
    note?: string | null;
    linkedWbsIds?: any;
    completionCriteria?: any;
};

export async function apiCreateContractMilestone(projectId: string, contractId: string, input: any) {
    const payload = {
        seq: input.seq,
        label: input.label,
        percent: input.percent ?? null,
        amountPlan: input.amountPlan ?? null,
        dueDate: input.dueDate ?? null,
        status: input.status ?? undefined,
        note: input.note ?? null,
        linkedWbsIds: input.linkedWbsIds ?? undefined,
        completionCriteria: input.completionCriteria ?? undefined,
    };

    // ✅ 최신 백엔드 mount 기준: /projects/:projectId/contracts
    const { data } = await api.post(`/projects/${projectId}/contracts/${contractId}/milestones`, payload);
    return normalizeMilestone(data?.data ?? data);
}

export type UpdateMilestonePatch = Partial<CreateMilestoneInput>;

export async function apiUpdateContractMilestone(
    projectId: string,
    contractId: string,
    milestoneId: string,
    patch: any,
) {
    const payload = {
        ...(patch.seq !== undefined ? { seq: patch.seq } : {}),
        ...(patch.label !== undefined ? { label: patch.label } : {}),
        ...(patch.percent !== undefined ? { percent: patch.percent ?? null } : {}),
        ...(patch.amountPlan !== undefined ? { amountPlan: patch.amountPlan ?? null } : {}),
        ...(patch.dueDate !== undefined ? { dueDate: patch.dueDate ?? null } : {}),
        ...(patch.status !== undefined ? { status: patch.status } : {}),
        ...(patch.note !== undefined ? { note: patch.note ?? null } : {}),
        ...(patch.linkedWbsIds !== undefined ? { linkedWbsIds: patch.linkedWbsIds ?? undefined } : {}),
        ...(patch.completionCriteria !== undefined
            ? { completionCriteria: patch.completionCriteria ?? undefined }
            : {}),
    };

    const { data } = await api.patch(
        `/projects/${projectId}/contracts/${contractId}/milestones/${milestoneId}`,
        payload,
    );
    return normalizeMilestone(data?.data ?? data);
}

export async function apiDeleteContractMilestone(projectId: string, contractId: string, milestoneId: string) {
    const { data } = await api.delete(`/projects/${projectId}/contracts/${contractId}/milestones/${milestoneId}`);
    return data?.data ?? data;
}

function normalizePayment(p: any): ActualPayment {
    return {
        ...p,
        amount: toNumberOrNull(p.amount) ?? 0,
        note: p.note ?? null,
    };
}

export async function apiCreateActualPayment(
    projectId: string,
    contractId: string,
    milestoneId: string,
    input: { amount: number; paidAt: string; note?: string | null },
) {
    const { data } = await api.post(
        `/projects/${projectId}/contracts/${contractId}/payments/milestones/${milestoneId}/payments`,
        input,
    );
    return normalizePayment(data?.data ?? data);
}

export async function apiUpdateActualPayment(
    projectId: string,
    contractId: string,
    paymentId: string,
    patch: { amount?: number; paidAt?: string; note?: string | null },
) {
    const { data } = await api.patch(`/projects/${projectId}/contracts/${contractId}/payments/${paymentId}`, patch);
    return normalizePayment(data?.data ?? data);
}

export async function apiDeleteActualPayment(projectId: string, contractId: string, paymentId: string) {
    const { data } = await api.delete(`/projects/${projectId}/contracts/${contractId}/payments/${paymentId}`);
    return data?.data ?? data;
}
